import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';
import { WorkforceOperationsNavComponent } from './workforce-operations-nav.component';

interface MonthDay {
  date: string;
  dayNumber: number;
  inMonth: boolean;
  weekday: string;
}

@Component({
  selector: 'app-admin-staff-calendar',
  standalone: true,
  imports: [CommonModule, FormsModule, WorkforceOperationsNavComponent],
  template: `
    <div class="min-h-full bg-[#eef3f8] p-4 md:p-5">
      <div class="flex flex-col xl:flex-row xl:items-end justify-between gap-3 mb-4">
        <div class="min-w-0">
          <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Workforce Operations</p>
          <h1 class="text-xl font-bold text-slate-950 tracking-tight leading-tight">Coverage Calendar</h1>
          <p class="text-slate-500 text-[11px]">Full month operations view for overall workload and each worker.</p>
        </div>
        <div class="flex flex-wrap items-end gap-2">
          <label class="block">
            <span class="block text-[10px] font-semibold text-slate-500 mb-1">Month</span>
            <input type="month" [ngModel]="monthInput()" (ngModelChange)="setMonth($event)" class="h-9 rounded-md border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-[#1A6FA6]">
          </label>
          <button type="button" (click)="moveMonth(-1)" class="w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:text-[#1A6FA6]" aria-label="Previous month"><i class="ti ti-chevron-left"></i></button>
          <button type="button" (click)="goToday()" class="h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-600 text-[11px] font-bold hover:text-[#1A6FA6]">This month</button>
          <button type="button" (click)="moveMonth(1)" class="w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:text-[#1A6FA6]" aria-label="Next month"><i class="ti ti-chevron-right"></i></button>
          <button type="button" (click)="loadCalendar()" [disabled]="loading()" class="h-9 px-4 rounded-md bg-[#0C3A5A] text-white text-[11px] font-bold hover:bg-[#082d46] disabled:opacity-60 flex items-center gap-2">
            <i class="ti" [class.ti-loader]="loading()" [class.animate-spin]="loading()" [class.ti-refresh]="!loading()"></i>
            Refresh
          </button>
        </div>
      </div>

      <app-workforce-operations-nav active="calendar"></app-workforce-operations-nav>

      <div class="mb-4 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-2">
        <div>
          <p class="text-[10px] font-bold uppercase tracking-[0.12em] text-[#1A6FA6]">Showing full month</p>
          <p class="text-[12px] font-semibold text-slate-800 mt-0.5">{{ monthLabel() }} · Overall operations and worker tabs</p>
        </div>
        <span class="inline-flex w-fit items-center gap-1.5 rounded-md bg-white border border-sky-100 px-2.5 py-1 text-[10px] font-bold text-slate-600">
          <i class="ti ti-calendar-month text-[#1A6FA6]"></i>
          Select a worker to view their full month
        </span>
      </div>

      <div class="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <div class="metric"><p>Field staff</p><strong>{{ staff().length }}</strong></div>
        <div class="metric"><p>Shift blocks</p><strong>{{ countEvents('shift') }}</strong></div>
        <div class="metric"><p>Job blocks</p><strong>{{ countEvents('job') }}</strong></div>
        <div class="metric"><p>Leave blocks</p><strong>{{ countEvents('leave') }}</strong></div>
        <div class="metric"><p>Project blocks</p><strong>{{ countEvents('project') }}</strong></div>
      </div>

      <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
        <div class="px-4 py-3 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <h2 class="text-[12px] font-bold text-slate-900">{{ selectedWorker() ? selectedWorker().name + ' operations' : 'Overall month operations' }}</h2>
            <p class="text-[10px] text-slate-500 mt-0.5">
              @if (selectedWorker()) {
                Full month schedule, work, leave, and project allocations for this worker.
              } @else {
                Daily totals across all field staff for the selected month.
              }
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            @for (item of legend; track item.label) {
              <span class="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500"><span class="w-2.5 h-2.5 rounded-full" [style.background]="item.color"></span>{{ item.label }}</span>
            }
          </div>
        </div>

        <div class="border-b border-slate-100 bg-slate-50/70 px-3 py-2 overflow-x-auto">
          <div class="flex gap-1.5 min-w-max">
            <button type="button" (click)="selectWorker('')" class="h-8 px-3 rounded-md border text-[11px] font-bold" [ngClass]="selectedWorkerId() === '' ? 'bg-[#0C3A5A] border-[#0C3A5A] text-white' : 'bg-white border-slate-200 text-slate-600 hover:text-[#1A6FA6]'">
              Overall
            </button>
            @for (person of staff(); track person.id) {
              <button type="button" (click)="selectWorker(person.id)" class="h-8 px-3 rounded-md border text-[11px] font-bold" [ngClass]="Number(selectedWorkerId()) === Number(person.id) ? 'bg-[#0C3A5A] border-[#0C3A5A] text-white' : 'bg-white border-slate-200 text-slate-600 hover:text-[#1A6FA6]'">
                {{ person.name }}
              </button>
            }
          </div>
        </div>

        @if (loading()) {
          <div class="p-10 text-center text-slate-500 text-sm"><i class="ti ti-loader animate-spin text-xl"></i><p class="mt-2">Loading staff calendar...</p></div>
        } @else {
          <div class="grid grid-cols-7 border-b border-slate-100 bg-slate-50/80">
            @for (day of weekDays; track day) {
              <div class="px-2 py-2 text-center text-[9px] font-bold uppercase tracking-wide text-slate-400 border-r border-slate-100">{{ day }}</div>
            }
          </div>

          <div class="grid grid-cols-7">
            @for (day of monthGridDays(); track day.date) {
              <div class="min-h-[142px] border-r border-b border-slate-100 p-2" [ngClass]="{
                'bg-white': day.inMonth && !isToday(day.date) && !workerHasLeave(day.date),
                'bg-sky-50/50': day.inMonth && isToday(day.date) && !workerHasLeave(day.date),
                'bg-red-50/50 ring-1 ring-inset ring-red-100': day.inMonth && workerHasLeave(day.date),
                'bg-slate-50/60 text-slate-400': !day.inMonth
              }">
                <div class="flex items-start justify-between gap-2">
                  <div>
                    <p class="text-[12px] font-bold" [ngClass]="day.inMonth ? 'text-slate-900' : 'text-slate-400'">{{ day.dayNumber }}</p>
                    <p class="text-[9px] text-slate-400">{{ day.weekday }}</p>
                  </div>
                  @if (day.inMonth && workerHasLeave(day.date)) {
                    <span class="px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-bold">Leave</span>
                  } @else if (day.inMonth && isToday(day.date)) {
                    <span class="px-1.5 py-0.5 rounded bg-[#1A6FA6] text-white text-[9px] font-bold">Today</span>
                  }
                </div>

                @if (selectedWorker()) {
                  <div class="mt-2 space-y-1.5">
                    @for (event of workerEventsFor(day.date); track eventKey(event)) {
                      <div class="rounded-md border px-2 py-1.5" [ngClass]="eventClass(event)">
                        <div class="flex items-center justify-between gap-2">
                          <p class="text-[10px] font-bold truncate">{{ eventTitle(event) }}</p>
                          <span class="text-[8px] font-bold uppercase shrink-0">{{ event.type === 'leave' ? event.status : event.type }}</span>
                        </div>
                        @if (event.time_label || event.detail) {
                          <p class="text-[9px] opacity-80 truncate mt-0.5">{{ eventDetail(event) }}</p>
                        }
                      </div>
                    } @empty {
                      <p class="mt-7 text-center text-[10px] text-slate-300">No roster item</p>
                    }
                  </div>
                } @else {
                  @if (day.inMonth) {
                    <div class="mt-2 grid grid-cols-2 gap-1">
                      <span class="summary-pill bg-emerald-50 text-emerald-700">{{ dailySummary(day.date).shift }} shifts</span>
                      <span class="summary-pill bg-sky-50 text-[#1A6FA6]">{{ dailySummary(day.date).job }} jobs</span>
                      <span class="summary-pill bg-red-50 text-red-700">{{ dailySummary(day.date).leave }} on leave</span>
                      <span class="summary-pill bg-violet-50 text-violet-700">{{ dailySummary(day.date).project }} projects</span>
                    </div>
                    <p class="mt-2 text-[10px] text-slate-500">
                      {{ dailySummary(day.date).staff }} worker{{ dailySummary(day.date).staff === 1 ? '' : 's' }} active/booked
                    </p>
                  }
                }
              </div>
            }
          </div>
        }
      </section>
    </div>
  `,
  styles: [`
    .metric { background: white; border: 1px solid #E7EEF5; border-radius: 8px; padding: 0.8rem 1rem; }
    .metric p { margin: 0; font-size: 10px; font-weight: 800; color: rgb(148 163 184); }
    .metric strong { display: block; margin-top: 0.35rem; font-size: 22px; line-height: 1; color: rgb(15 23 42); }
    .summary-pill { border-radius: 0.375rem; padding: 0.25rem 0.375rem; font-size: 10px; font-weight: 700; min-height: 1.5rem; }
  `]
})
export class AdminStaffCalendarComponent implements OnInit {
  Number = Number;
  private api = inject(ApiService);

  loading = signal(false);
  staff = signal<any[]>([]);
  eventMap = signal<Record<string, any[]>>({});
  selectedWorkerId = signal<string | number>('');

  filters = {
    from: this.monthStartIso(new Date()),
    to: this.monthEndIso(new Date())
  };

  weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  legend = [
    { label: 'Shift', color: '#059669' },
    { label: 'Job', color: '#2563eb' },
    { label: 'Leave', color: '#dc2626' },
    { label: 'Project', color: '#7c3aed' }
  ];

  ngOnInit() {
    this.loadCalendar();
  }

  loadCalendar() {
    if (!this.filters.from || !this.filters.to || this.filters.from > this.filters.to) {
      showToast('error', 'Select a valid month.');
      return;
    }

    this.loading.set(true);
    this.api.getStaffCalendar({ from: this.filters.from, to: this.filters.to }).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res.success && res.data) {
          this.staff.set(res.data.staff || []);
          this.eventMap.set(res.data.events || {});
          if (this.selectedWorkerId() && !this.selectedWorker()) {
            this.selectedWorkerId.set('');
          }
        } else {
          showToast('error', res.message || 'Staff calendar could not be loaded.');
        }
      },
      error: (err: any) => {
        this.loading.set(false);
        showToast('error', err?.error?.message || 'Staff calendar could not be loaded.');
      }
    });
  }

  selectWorker(workerId: string | number) {
    this.selectedWorkerId.set(workerId || '');
  }

  selectedWorker(): any | null {
    const selected = Number(this.selectedWorkerId() || 0);
    if (!selected) return null;
    return this.staff().find(person => Number(person.id) === selected) || null;
  }

  setMonth(value: string) {
    if (!/^\d{4}-\d{2}$/.test(value || '')) return;
    const [year, month] = value.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    this.filters.from = this.monthStartIso(date);
    this.filters.to = this.monthEndIso(date);
    this.loadCalendar();
  }

  moveMonth(direction: number) {
    const base = this.parseIsoDate(this.filters.from) || new Date();
    base.setMonth(base.getMonth() + direction);
    this.filters.from = this.monthStartIso(base);
    this.filters.to = this.monthEndIso(base);
    this.loadCalendar();
  }

  goToday() {
    const today = new Date();
    this.filters.from = this.monthStartIso(today);
    this.filters.to = this.monthEndIso(today);
    this.loadCalendar();
  }

  monthInput(): string {
    return this.filters.from.slice(0, 7);
  }

  monthLabel(): string {
    const date = this.parseIsoDate(this.filters.from);
    return date ? date.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' }) : this.filters.from;
  }

  monthGridDays(): MonthDay[] {
    const monthStart = this.parseIsoDate(this.filters.from) || new Date();
    const monthEnd = this.parseIsoDate(this.filters.to) || new Date(monthStart);
    const gridStart = new Date(monthStart);
    gridStart.setDate(gridStart.getDate() - ((gridStart.getDay() + 6) % 7));

    const gridEnd = new Date(monthEnd);
    gridEnd.setDate(gridEnd.getDate() + (6 - ((gridEnd.getDay() + 6) % 7)));

    const days: MonthDay[] = [];
    const cursor = new Date(gridStart);
    while (cursor <= gridEnd) {
      days.push({
        date: this.toIso(cursor),
        dayNumber: cursor.getDate(),
        inMonth: cursor >= monthStart && cursor <= monthEnd,
        weekday: cursor.toLocaleDateString('en-NZ', { weekday: 'short' })
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }

  workerEventsFor(date: string): any[] {
    const selected = Number(this.selectedWorkerId() || 0);
    if (!selected) return [];
    return [...(this.eventMap()[`${selected}|${date}`] || [])].sort((a, b) => this.eventPriority(a) - this.eventPriority(b));
  }

  workerHasLeave(date: string): boolean {
    return this.workerEventsFor(date).some(event => event.type === 'leave');
  }

  dailySummary(date: string): { shift: number; job: number; leave: number; project: number; staff: number } {
    const events = this.eventsForDate(date);
    const staffIds = new Set(events.map(event => Number(event.staff_id || 0)).filter(Boolean));
    return {
      shift: events.filter(event => event.type === 'shift').length,
      job: events.filter(event => event.type === 'job').length,
      leave: events.filter(event => event.type === 'leave').length,
      project: events.filter(event => event.type === 'project').length,
      staff: staffIds.size
    };
  }

  eventsForDate(date: string): any[] {
    return Object.entries(this.eventMap())
      .filter(([key]) => key.endsWith(`|${date}`))
      .flatMap(([, events]) => events || []);
  }

  countEvents(type: string): number {
    return Object.values(this.eventMap()).flat().filter((event: any) => event.type === type).length;
  }

  eventKey(event: any): string {
    return `${event.staff_id || ''}-${event.type}-${event.reference_id || ''}-${event.title || ''}-${event.date || ''}`;
  }

  eventTitle(event: any): string {
    if (event.type === 'leave') {
      return `${event.title || 'Leave'} leave`;
    }
    return event.title || 'Roster item';
  }

  eventDetail(event: any): string {
    if (event.type === 'leave') {
      return `${event.status || 'Leave'} · ${event.detail || 'Full day'}`;
    }
    return event.time_label || event.detail || '';
  }

  isToday(value: string): boolean {
    return value === this.toIso(new Date());
  }

  eventClass(event: any): string {
    if (event.type === 'shift') return event.status === 'active' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-green-50 border-green-100 text-green-800';
    if (event.type === 'job') return event.priority === 'Urgent' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-sky-50 border-sky-100 text-sky-800';
    if (event.type === 'leave') return event.status === 'Pending' ? 'bg-amber-100 border-amber-300 text-amber-900' : 'bg-red-100 border-red-300 text-red-900';
    if (event.type === 'project') return 'bg-violet-50 border-violet-100 text-violet-800';
    return 'bg-slate-50 border-slate-100 text-slate-700';
  }

  private eventPriority(event: any): number {
    if (event.type === 'leave') return 1;
    if (event.type === 'shift') return 2;
    if (event.type === 'job') return 3;
    if (event.type === 'project') return 4;
    return 5;
  }

  private monthStartIso(date: Date): string {
    return this.toIso(new Date(date.getFullYear(), date.getMonth(), 1));
  }

  private monthEndIso(date: Date): string {
    return this.toIso(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  }

  private parseIsoDate(value: string): Date | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private toIso(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
